/**
 * LazyVideo — Landing V2 cinematic video player.
 *
 * Plays a muted, looped video ONLY while it's in viewport
 * (IntersectionObserver, threshold=0.25). Pauses out of view.
 *
 * Respects `prefers-reduced-motion`: when enabled, renders the
 * poster image as a static <img> and doesn't load the video bytes
 * at all — accessibility + bandwidth win.
 *
 * Browser source order: <source type="video/webm"> first (VP9, smaller),
 * <source type="video/mp4"> second (H.264, universal fallback). The
 * browser picks the first it can play; Safari < 14 falls through to MP4
 * automatically.
 *
 * Usage:
 *   <LazyVideo
 *     sources={{ mp4: '...720p.mp4', webm: '...720p.webm' }}
 *     poster="...poster.jpg"
 *     className="absolute inset-0 w-full h-full object-cover"
 *     ariaLabel="DealFlow swipe demo"
 *   />
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useReducedMotion } from 'framer-motion';

export default function LazyVideo({
  sources = {},
  poster,
  className = '',
  ariaLabel = '',
  // Forwarded to the underlying <video> tag (testID, onLoadedData, etc.)
  ...rest
}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const reducedMotion = useReducedMotion();

  // play() may reject when the browser blocks autoplay (rare for muted
  // + playsInline videos, but defensive). Swallow the rejection — the
  // poster will show, that's an acceptable degraded state.
  const safePlay = useCallback(() => {
    const v = ref.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  useEffect(() => {
    if (reducedMotion || !ref.current) return undefined;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            safePlay();
          } else {
            el.pause();
          }
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reducedMotion, safePlay]);

  // Reduced-motion: never load the video bytes — show poster only.
  if (reducedMotion) {
    return (
      <img
        src={poster}
        alt={ariaLabel}
        className={className}
        loading="lazy"
        {...rest}
      />
    );
  }

  return (
    <video
      ref={ref}
      poster={poster}
      muted
      loop
      playsInline
      // `preload="metadata"` lets the browser fetch the first frame +
      // duration without downloading the whole file — important for
      // mobile data budgets.
      preload="metadata"
      aria-label={ariaLabel}
      className={className}
      {...rest}
    >
      {sources.webm && <source src={sources.webm} type="video/webm" />}
      {sources.mp4 && <source src={sources.mp4} type="video/mp4" />}
      {/* Plain-text fallback for assistive tech if neither source
          decodable. The poster <img> would still render as a frame. */}
    </video>
  );
}
